// The Layout Canvas command palette (Ctrl+K): find any tool, condition, drawing, scale or action by typing.
//
// Pure: `buildPaletteItems` turns what the canvas knows (its state and the handlers it already has) into a flat
// list of items; `searchPalette` ranks them against what was typed; `groupResults` orders them for display. The
// palette adds NO new behavior — every item calls the same function its toolbar button or key already calls — and
// an item that cannot run right now stays visible, greyed, with the reason, so the palette is also a way to
// discover what is available and why it is not.

export const PALETTE_GROUPS = ["Tools", "Conditions", "Drawings", "Scale", "Edit", "View", "Crosshair", "Help"];

const norm = (s) => String(s ?? "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");

/** Score one item for the typed tokens; null = does not match. Every token must match somewhere. */
export function scoreItem(item, tokens) {
  if (!tokens.length) return 0;
  const label = norm(item.label);
  const words = label.split(/[^a-z0-9]+/).filter(Boolean);
  const rest = norm([item.hint, item.group, ...(item.keywords || [])].join(" "));
  let total = 0;
  for (const t of tokens) {
    if (label === t) total += 120;
    else if (label.startsWith(t)) total += 100;
    else if (words.some(w => w.startsWith(t))) total += 80;
    else if (label.includes(t)) total += 50;
    else if (rest.includes(t)) total += 20;
    else return null;
  }
  return total;
}

/** Ranked matches (best first; ties keep list order). An empty query returns everything in list order. */
export function searchPalette(items, query, { limit = 80 } = {}) {
  const tokens = norm(query).split(/\s+/).filter(Boolean);
  const scored = [];
  items.forEach((item, i) => { const s = scoreItem(item, tokens); if (s != null) scored.push({ item, s, i }); });
  scored.sort((a, b) => b.s - a.s || a.i - b.i);
  return scored.slice(0, limit).map(x => x.item);
}

/** Results in display order. A typed query keeps rank order (one flat list); an empty one is grouped. */
export function groupResults(results, { grouped = true } = {}) {
  if (!grouped) return [{ group: null, items: results }];
  const out = [];
  for (const g of PALETTE_GROUPS) {
    const items = results.filter(r => r.group === g);
    if (items.length) out.push({ group: g, items });
  }
  return out;
}

const sheetLabel = (s) => (s.SheetNumber ? `${s.SheetNumber}${s.SheetName ? ` — ${s.SheetName}` : ""}` : (s.SheetName || `Drawing ${s.SheetID}`));
const STYLE_VERB = { linear: "Trace", area: "Trace area", count: "Place" };

/**
 * The palette's items. `a`:
 *   locked, needsScale                booleans — why an item is unavailable
 *   flags { drawMode, measureMode, calib, imgMode, sweepMode, hasSelection, hasDrawing, isPdf }
 *   undo  { canUndo, canRedo, undoLabel, redoLabel }
 *   conditionTypes                    [{ TypeKey, Code, Name, Style }]
 *   sheets, activeSheetId             the drawings
 *   scales                            [{ label, ftPerInch }] (architectural + engineering)
 *   dark, crosshair, layers           view state; layerDefs [{ key, label }]
 *   run                               { pointer, drawRoom, measure, calibrate, moveDrawing, rotateDrawingCw, rotateDrawingCcw, sweep, search, backOut,
 *                                       armType(type), selectSheet(id), setScale(ftPerInch), undo, redo, fit,
 *                                       zoomSelection, toggleDark, setCrosshair(style), toggleLayer(key) }
 */
export function buildPaletteItems(a) {
  const f = a.flags || {};
  const ro = a.locked ? "This revision is read-only" : null;
  const scaleFirst = a.needsScale ? "Set this drawing's scale first" : null;
  const items = [];
  const add = (group, id, label, run, extra = {}) => items.push({ group, id, label, run, hint: "", keywords: [], shortcut: "", disabled: null, ...extra });

  // ── tools ──
  add("Tools", "tool:pointer", "Select (pointer)", a.run.pointer, { hint: "Stop the current tool", keywords: ["cursor", "escape", "stop"], shortcut: "Esc" });
  add("Tools", "tool:room", f.drawMode ? "Draw room — stop" : "Draw room", a.run.drawRoom, { hint: "Drag a box or click corners", keywords: ["room", "outline", "trace"], shortcut: "D", disabled: ro || scaleFirst });
  add("Tools", "tool:measure", f.measureMode ? "Measure — stop" : "Measure", a.run.measure, { hint: "Drag between two points to read a distance", keywords: ["ruler", "distance", "length"], disabled: scaleFirst });
  add("Tools", "tool:calibrate", f.isPdf ? "Verify scale" : "Set scale (two points)", a.run.calibrate, { hint: "Click two points of a known dimension", keywords: ["calibrate", "scale"], disabled: ro });
  add("Tools", "tool:move", "Move drawing", a.run.moveDrawing, { hint: "Drag the drawing under your rooms", keywords: ["reposition", "image", "pdf", "pan"], disabled: ro });
  if (a.run.rotateDrawingCw) add("Tools", "tool:rotate-cw", "Rotate drawing clockwise", a.run.rotateDrawingCw, { hint: "Turn the drawing 90° (only before anything is drawn on it)", keywords: ["turn", "orientation", "sideways", "landscape", "portrait", "pdf", "image"], disabled: ro });
  if (a.run.rotateDrawingCcw) add("Tools", "tool:rotate-ccw", "Rotate drawing counter-clockwise", a.run.rotateDrawingCcw, { hint: "Turn the drawing 90° (only before anything is drawn on it)", keywords: ["turn", "orientation", "sideways", "landscape", "portrait", "pdf", "image"], disabled: ro });
  if (a.run.sweep) add("Tools", "tool:sweep", "Count by example", a.run.sweep, { hint: "Box one symbol, count its repeats", keywords: ["symbol", "sweep", "count", "find similar", "repeat"], disabled: scaleFirst });
  add("Tools", "tool:backout", "Back-Out…", a.run.backOut, { hint: "Deduct an area from a room element", keywords: ["deduct", "cut", "opening", "exclude"], disabled: ro });
  add("Tools", "tool:search", "Search drawings", a.run.search, { hint: "Find printed text on every drawing", keywords: ["find", "text", "tag", "note"] });

  // ── conditions ──
  for (const t of a.conditionTypes || []) {
    const verb = STYLE_VERB[t.Style] || "Trace";
    add("Conditions", `cond:${t.TypeKey}`, `${verb} ${t.Name || t.Code || "condition"}`, () => a.run.armType(t), {
      hint: t.Code ? `${t.Code} · ${t.Style || "linear"}` : (t.Style || "linear"), keywords: [t.Code, t.Style, "condition", "draw"].filter(Boolean), disabled: ro || scaleFirst,
    });
    if (a.run.togglePin) {
      const pinned = (a.pins || []).includes(t.TypeKey);
      add("Conditions", `pin:${t.TypeKey}`, `${pinned ? "Unpin" : "Pin"} ${t.Name || t.Code || "condition"} ${pinned ? "from" : "to"} the strip`, () => a.run.togglePin(t.TypeKey), {
        hint: "Pinned conditions are armed with keys 1–9", keywords: ["pin", "strip", "favorite", "shortcut", t.Code].filter(Boolean),
      });
    }
  }

  // ── drawings ──
  for (const s of a.sheets || []) {
    const here = s.SheetID === a.activeSheetId;
    add("Drawings", `sheet:${s.SheetID}`, `Go to ${sheetLabel(s)}`, () => a.run.selectSheet(s.SheetID), { hint: here ? "Current drawing" : (s.IsKeySheet ? "★ Key sheet" : s.Discipline || "Drawing"), keywords: [s.SheetNumber, s.SheetName, s.Discipline, "sheet", "drawing", "page", ...(s.IsKeySheet ? ["key", "star", "starred", "important"] : [])].filter(Boolean), disabled: here ? "Already on this drawing" : null });
  }

  // ── scale ──
  if (f.isPdf && f.hasDrawing) {
    for (const sc of a.scales || []) add("Scale", `scale:${sc.label}`, `Set scale ${sc.label}`, () => a.run.setScale(sc.ftPerInch), { hint: "Calibrates the drawing to feet", keywords: ["scale", "calibrate"], disabled: ro });
  }

  // ── edit ──
  add("Edit", "edit:undo", a.undo?.undoLabel ? `Undo ${a.undo.undoLabel}` : "Undo", a.run.undo, { shortcut: "Ctrl+Z", disabled: ro || (a.undo?.canUndo ? null : "Nothing to undo"), keywords: ["revert", "back"] });
  add("Edit", "edit:redo", a.undo?.redoLabel ? `Redo ${a.undo.redoLabel}` : "Redo", a.run.redo, { shortcut: "Ctrl+Shift+Z", disabled: ro || (a.undo?.canRedo ? null : "Nothing to redo"), keywords: ["again", "forward"] });

  // ── view ──
  add("View", "view:fit", "Fit drawing to window", a.run.fit, { shortcut: "F", keywords: ["zoom", "all", "extents"] });
  add("View", "view:zoomsel", "Zoom to selection", a.run.zoomSelection, { shortcut: "Z", disabled: f.hasSelection ? null : "Select a room first", keywords: ["frame", "focus"] });
  if (a.run.toggleUnits) add("View", "view:units", a.units === "metric" ? "Units: switch to feet and inches" : "Units: switch to metric (m, m²)", a.run.toggleUnits, { hint: "Display only — everything is stored in feet", keywords: ["metric", "imperial", "metres", "meters", "millimetres", "feet", "inches", "units", "mm"] });
  add("View", "view:dark", a.dark ? "Switch to light drawing" : "Switch to dark drawing", a.run.toggleDark, { hint: "The plan only", keywords: ["theme", "night", "invert", "colour", "color", "plan", "pdf"] });
  if (a.run.toggleFocus) add("View", "view:focus", a.focusOn ? "Exit focus mode" : "Focus mode — drawing only", a.run.toggleFocus, { shortcut: "M", hint: "Hide the ribbon and panels; M or Esc brings them back", keywords: ["maximize", "full screen", "fullscreen", "hide", "panels", "ribbon", "small screen", "surface", "distraction"] });
  if (a.run.toggleLegend) add("View", "view:legend", a.legendOn ? "Hide the legend" : "Show the legend", a.run.toggleLegend, { hint: "Conditions and quantities over the drawing", keywords: ["key", "quantities", "conditions", "overlay"] });
  if (a.run.toggleUiDark) add("View", "view:ui-dark", a.uiDark ? "Switch to light interface" : "Switch to dark interface", a.run.toggleUiDark, { hint: "Toolbars, panels and windows", keywords: ["theme", "night", "dark mode", "light mode", "colour", "color", "screen", "ui"] });

  // ── crosshair ──
  for (const [k, label] of [["bold", "Bold"], ["standard", "Standard"], ["off", "Off"]]) {
    add("Crosshair", `xh:${k}`, `Crosshair: ${label}`, () => a.run.setCrosshair(k), { hint: a.crosshair === k ? "Current" : "", keywords: ["cursor", "guide", "lines"], disabled: a.crosshair === k ? "Already set" : null });
  }
  for (const l of a.layerDefs || []) {
    const on = !!a.layers?.[l.key];
    add("Crosshair", `xl:${l.key}`, `Crosshair layer: ${l.label} — turn ${on ? "off" : "on"}`, () => a.run.toggleLayer(l.key), { keywords: ["cursor", "snap", "angle", "layer"] });
  }

  // ── help ──
  if (a.run.guide) add("Help", "help:shortcuts", "Keyboard shortcuts", a.run.guide, { shortcut: "?", keywords: ["keys", "keyboard", "help", "hotkeys", "bindings", "guide"] });
  return items;
}
